import { useRef, useState, useEffect, useCallback } from "react";
import { Box, Button, ChakraProvider, HStack, Tooltip, useDisclosure, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, IconButton, Text } from "@chakra-ui/react";
import { Editor } from "@monaco-editor/react";
import LanguageSelector from "./LanguageSelector";
import { CODE_SNIPPETS } from "../constants";
import Output from "./Output";
import theme from "../../theme";
import { useWindowSize } from "../../useWindowSize";
import { useWebSocket } from "../../WebSocketContext";
import { toast } from "react-hot-toast";
import ConfirmationModal from "../../ConfirmationModal";
import _ from "lodash";
import logger from './../../utils/logger.js';




const CodeEditor = ({ onClose, onChangeInputValue, onSubmit, initialValue, question_remarks, onLanguageChange, onTimerChange, interviewId}) => {
  const editorRef = useRef();
  const {
    ws,
    sendDebugHintRequest,
    sendApproachHintRequest,
    sendExplanationRequest,
    sendSkipRequest,
    setCodeLanguage,
    question,
    id,
    questionTimer,
    codeLanguage,
    hintsUsed,
    MAX_HINTS_PER_INTERVIEW,
    
  } = useWebSocket();
  

  useEffect(() => {
    logger.debug("WebSocket state:", {
      ws,
      codeLanguage,
      question,
      id
    });
  }, [ws, codeLanguage, question, id]);


  // Function to check if buttons should be hidden for specific question IDs
  const shouldHideButtons = () => {
    const hideButtonsForIds = ["6840374a86c7ef3718cac46b", "683df11b1e69988efc16d27b", "68497a7467721b93b2ab0a78","68497a6a596aec5980a52ea7"];
    return hideButtonsForIds.includes(id);
  };
  
  // Function to check if copy/paste should be allowed for specific question IDs
  const shouldAllowCopyPaste = () => {
    const allowCopyPasteForIds = ["683df11b1e69988efc16d27b"];
    return allowCopyPasteForIds.includes(id);
  };
  
  // Initialize language with a function that checks localStorage first
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved language
    const questionId = id || (question && question.id);
    const interviewKey = interviewId && questionId ? `language_${interviewId}_${questionId}` : null;
    const savedLanguage = interviewKey ? localStorage.getItem(interviewKey) : null;
    
    // If codeLanguage is 'all', don't use it as a language
    const defaultLanguage = (codeLanguage && codeLanguage !== 'all') ? codeLanguage : 'javascript';
    
    // Use saved language or fall back to codeLanguage (from context) or default to JavaScript
    logger.debug('Initial language load:', {
      savedLanguage,
      defaultLanguage,
      codeLanguage,
      finalLanguage: savedLanguage || defaultLanguage
    });
    return savedLanguage || defaultLanguage;
  });
  
  const [isSqlMode, setIsSqlMode] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width < 700;
  const { isOpen, onOpen, onClose: onAlertClose } = useDisclosure();
  const cancelRef = useRef();
  // Update useEffect to check if initial language is SQL
  useEffect(() => {
    // We're no longer setting SQL mode based on language type
    // This ensures the language selector never gets locked
    logger.debug("Language changed to:", codeLanguage);
  }, [codeLanguage]);

  // Define getInitialValue function before it's used
  const getInitialValue = (lang, remarks) => {
    let commentSymbol;
    switch (lang.toLowerCase()) {
      case 'python':
        commentSymbol = '#';
        break;
      default:
        commentSymbol = '//';
    }

    // Check if remarks already starts with a comment symbol
    const remarksWithComment = remarks?.trimStart().startsWith(commentSymbol) ?
      remarks :
      `${commentSymbol} ${remarks}`;

    return remarks ?
      `${remarksWithComment}\n\n${CODE_SNIPPETS[lang] || ''}` :
      CODE_SNIPPETS[lang] || '';
  };

  // Helper function to get localStorage key with proper namespacing
  const getLocalStorageKey = (type) => {
    // Make sure we have a question ID - either from the WebSocket context or from the question prop
    const questionId = id || (question && question.id);
    if (!questionId || !interviewId) {
      logger.debug('Missing questionId or interviewId for localStorage key');
      return null;
    }
    return `codeEditor_${type}_${interviewId}_${questionId}`;
  };

  // Initialize code value from localStorage or fallback to initialValue/default
  const [value, setValue] = useState(() => {
    // Try to load saved code from localStorage first
    const storageKey = getLocalStorageKey('code');
    if (storageKey) {
      const savedCode = localStorage.getItem(storageKey);
      if (savedCode) {
        logger.debug('Loaded saved code from localStorage with key:', storageKey);
        return savedCode;
      }
    }
    
    // Fallback to initialValue or default template
    if (initialValue) {
      logger.debug('Using initialValue as no saved code found');
      return initialValue;
    }
    logger.debug('Using default template as no saved code or initialValue found');
    return getInitialValue(language, question_remarks);
  });

  const [isSkipDisabled, setIsSkipDisabled] = useState(false);
  const [isHintClicked, setIsHintClicked] = useState(false);
  const [isExplainClicked, setIsExplainClicked] = useState(false);
  const [boilerplateCodes, setBoilerplateCodes] = useState({});
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [isExplainDisabled, setIsExplainDisabled] = useState(false);
  const [isHintModalOpen, setIsHintModalOpen] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isHintDisabled, setIsHintDisabled] = useState(false); // New state to track hint button disable status
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(Date.now());
  const [countdown, setCountdown] = useState(null);
  const [isTimeWarningOpen, setIsTimeWarningOpen] = useState(false);
  const [warningTimeMinutes, setWarningTimeMinutes] = useState(5); // Default to 5 minutes
  const [hasShownFiveMinWarning, setHasShownFiveMinWarning] = useState(false);
  const [hasShownOneMinWarning, setHasShownOneMinWarning] = useState(false);

  const timerIntervalRef = useRef(null);
  const countdownRef = useRef(countdown);
  const valueRef = useRef(value);
  const languageRef = useRef(language);
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

  // Track submitted questions per interview
  const [submittedQuestions, setSubmittedQuestions] = useState(() => {
    const saved = localStorage.getItem(`submittedQuestions_${interviewId}`);
    return saved ? JSON.parse(saved) : {};
  });

  // Check if current question is submitted for this interview
  const isQuestionSubmitted = id && submittedQuestions[id];

  // Initialize isSubmitted state based on localStorage data
  const [isSubmitted, setIsSubmitted] = useState(() => {
    return isQuestionSubmitted || false;
  });

  // Update refs when state changes
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  // Initialize warning states from localStorage
  useEffect(() => {
    if (id) {
      // Check if we've already shown warnings for this question
      const fiveMinWarningShown = localStorage.getItem(`warning_5min_shown_${id}`);
      const oneMinWarningShown = localStorage.getItem(`warning_1min_shown_${id}`);
      
      if (fiveMinWarningShown === 'true') {
        setHasShownFiveMinWarning(true);
      }
      
      if (oneMinWarningShown === 'true') {
        setHasShownOneMinWarning(true);
      }
    }
  }, [id]);

  // Update localStorage when submittedQuestions changes
  useEffect(() => {
    if (interviewId) {
      localStorage.setItem(`submittedQuestions_${interviewId}`, JSON.stringify(submittedQuestions));
    }
  }, [submittedQuestions, interviewId]);

  // Clear submitted questions when interview changes
  useEffect(() => {
    if (interviewId) {
      const savedForInterview = localStorage.getItem(`submittedQuestions_${interviewId}`);
      if (savedForInterview) {
        setSubmittedQuestions(JSON.parse(savedForInterview));
      } else {
        setSubmittedQuestions({});
      }
    }
  }, [interviewId]);

  // Format seconds to mm:ss
  const formatSecondsToTime = (totalSeconds) => {
    if (totalSeconds <= 0) return "00:00";
    
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Parse time string (mm:ss) to seconds
  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    
    if (typeof timeString === 'number') {
      return timeString;
    }
    
    const parts = timeString.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);
      if (!isNaN(minutes) && !isNaN(seconds)) {
        return minutes * 60 + seconds;
      }
    } else if (parts.length === 1) {
      const seconds = parseInt(parts[0], 10);
      if (!isNaN(seconds)) {
        return seconds;
      }
    }
    return 0;
  };

  // Function to save timer state to localStorage
  const saveTimerState = (seconds, formattedTime) => {
    // Save the timer value to localStorage with the specific key for this question
    const timerKey = getLocalStorageKey('timer');
    if (timerKey) {
      localStorage.setItem(timerKey, seconds.toString());
      // logger.debug('Saved timer state to localStorage:', seconds, 'seconds with key:', timerKey);
    } else {
      logger.warn('Could not save timer state - missing storage key');
    }
  };

  // Function to clear timer state from localStorage
  const clearTimerState = () => {
    const timerKey = getLocalStorageKey('timer');
    if (timerKey) {
      localStorage.removeItem(timerKey);
      logger.debug('Cleared timer state from localStorage with key:', timerKey);
    } else {
      logger.warn('Could not clear timer state - missing storage key');
    }
  };

  // A simplified countdown logic for debugging
  const startSimpleCountdown = (initialSeconds) => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Use provided initialSeconds or parse from the current countdown state
    let totalSeconds;
    
    if (initialSeconds !== undefined) {
      totalSeconds = initialSeconds;
    } else {
      // Parse the initial time - add more validation
      const timeString = questionTimer || "30:00"; // Default to 30 minutes if no timer
      
      // Make sure we have a valid time format
      if (!timeString) {
        return;
      }
      
      if (typeof timeString === 'number') {
        totalSeconds = timeString;
      } else if (timeString.includes(':')) {
        const [minutes, seconds] = timeString.split(':').map(num => parseInt(num, 10));
        if (isNaN(minutes) || isNaN(seconds)) {
          return;
        }
        totalSeconds = (minutes * 60) + seconds;
      } else {
        totalSeconds = parseInt(timeString, 10);
        if (isNaN(totalSeconds)) {
          return;
        }
      }
    }
    
    const originalTotalSeconds = totalSeconds; // Store the original time for reference
    
    // Save the initial timer value immediately
    saveTimerState(totalSeconds, formatSecondsToTime(totalSeconds));
    logger.debug('Saved initial timer value to localStorage:', totalSeconds);
    
    // Update the UI immediately with properly formatted time
    setCountdown(formatSecondsToTime(totalSeconds));
    
    // Call onTimerChange with initial value
    if (onTimerChange) {
      onTimerChange({
        seconds: totalSeconds,
        displayValue: formatSecondsToTime(totalSeconds)
      });
    }
    
    // Track last update time to detect stalled timers
    let lastUpdateTime = Date.now();
    // Track last auto-save time
    let lastAutoSaveTime = Date.now();
    // Flag to track if auto-submission has occurred
    let hasAutoSubmitted = false;
    // Track if timer is paused (during auto-save operations)
    let isTimerPaused = false;
    
    // Start a simple interval that decrements every second
    timerIntervalRef.current = setInterval(() => {
      // Skip decrementing if timer is paused
      if (isTimerPaused) {
        // Just update the last update time to prevent stall detection
        lastUpdateTime = Date.now();
        return;
      }
      
      // Check if timer has stalled (more than 2 seconds since last update)
      const currentTime = Date.now();
      const timeSinceLastUpdate = currentTime - lastUpdateTime;
      
      // If timer seems to have stalled, adjust the decrement accordingly
      if (timeSinceLastUpdate > 2000) {
        const missedSeconds = Math.floor(timeSinceLastUpdate / 1000);
        totalSeconds -= missedSeconds;
      } else {
        totalSeconds -= 1;
      }
      
      // Update last update time
      lastUpdateTime = currentTime;
      
      // Check if it's time to auto-save (every 3 minutes = 180 seconds)
      const timeSinceLastAutoSave = currentTime - lastAutoSaveTime;
      const autoSaveIntervalMs = 3 * 60 * 1000; // 3 minutes in milliseconds
      
      // // Only auto-save if we're not close to auto-submission and haven't already auto-submitted
      // if (timeSinceLastAutoSave >= autoSaveIntervalMs && !hasAutoSubmitted && totalSeconds > 10) {
      //   // Only auto-save if we have valid code
      //   if (value && value.trim() !== '') {
      //     // Temporarily pause the timer during auto-save
      //     isTimerPaused = true;
          
      //     // Perform auto-save without blocking the UI
      //     Promise.resolve().then(() => {
      //       if (import.meta.env.MODE !== 'production') {
      //         logger.debug('Auto-saving code (3-minute interval)');
      //       }
            
      //       // Execute auto-save and resume timer afterward
      //       try {
      //         autoSaveCode();
      //       } finally {
      //         // Resume timer after auto-save completes or fails
      //         isTimerPaused = false;
      //         // Update last auto-save time
      //         lastAutoSaveTime = Date.now();
      //       }
      //     });
      //   }
      // }
      
      if (totalSeconds <= 0) {
        // Set the auto-submitted flag to prevent further auto-saves
        hasAutoSubmitted = true;
        
        // Ensure interval is properly cleared from memory
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        setCountdown("00:00");
        
        // Save final timer state to localStorage
        saveTimerState(0, "00:00");
        
        // Call onTimerChange with 0
        if (onTimerChange) {
          onTimerChange({
            seconds: 0,
            displayValue: "00:00"
          });
        }
        
        // Handle time's up
        toast.error("Time's up! Submitting your code...");
        
        // Disable submit and hint buttons when timer expires
        setIsSubmitDisabled(true);
        setIsHintDisabled(true);
        
        // Set auto-submitted flag to prevent confirmation modal
        const isAutoSubmit = true;
        
        // Add detailed logging for auto-submission
        logger.debug('Timer expired - Auto-submitting code with data:', {
          value: valueRef.current,
          language: languageRef.current,
          id: id,
          question: question,
          interviewId: interviewId,
          isAutoSubmit: true
        });
        
        // Call your submission function here with a slight delay
        if (onSubmit) {
          // Use setTimeout to ensure the timer state is updated before submission
          setTimeout(() => {
            logger.debug('Executing auto-submission with data:', {
              value: valueRef.current,
              language: languageRef.current,
              id: id,
              question: question,
              interviewId: interviewId
            });
            
            // Add more detailed logging for the submission process
            logger.debug('About to call onSubmit with:', {
              isAutoSubmit: true,
              timerExpired: true,
              value: valueRef.current,
              language: languageRef.current
            });
            
            try {
              const submissionData = {
                isAutoSubmit: true,
                preventDefault: () => {},
                timerExpired: true,
                value: valueRef.current,
                language: languageRef.current,
                questionId: id,
                interviewId: interviewId
              };
              
              logger.debug('Submitting with data:', submissionData);
              onSubmit(submissionData);
              logger.debug('onSubmit called successfully');
            } catch (error) {
              logger.error('Error in onSubmit:', error);
            }
          }, 100);
        } else {
          logger.error('onSubmit function is not defined');
        }
        return;
      }
      
      // Update the UI with properly formatted time
      const newTime = formatSecondsToTime(totalSeconds);
      setCountdown(newTime);
      
      // Save current timer state to localStorage
      saveTimerState(totalSeconds, newTime);
      
      // Call onTimerChange with updated value
      if (onTimerChange) {
        onTimerChange({
          seconds: totalSeconds,
          displayValue: newTime
        });
      }
      
      // Show warnings at specific time points
      if (totalSeconds === 300 && !hasShownFiveMinWarning) {
        setHasShownFiveMinWarning(true);
        setWarningTimeMinutes(5);
        setIsTimeWarningOpen(true);
        toast("5 minutes remaining!");
      } else if (totalSeconds === 60 && !hasShownOneMinWarning) {
        setHasShownOneMinWarning(true);
        setWarningTimeMinutes(1);
        setIsTimeWarningOpen(true);
        toast("1 minute remaining!");
      } else if (totalSeconds === 30) {
        toast("30 seconds remaining!");
      } else if (totalSeconds === 10) {
        toast("10 seconds remaining!");
      }
    }, 1000);
  };

  // Replace your startCountdown with this simpler version for testing
  // const startCountdown = startSimpleCountdown;

  // Initialize the countdown from the questionTimer or saved timer value
  useEffect(() => {
    let timerId = null;
    
    // Always clear existing interval first
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Check for saved timer value first
    const savedTimerKey = getLocalStorageKey('timer');
    const savedTimerValue = savedTimerKey ? localStorage.getItem(savedTimerKey) : null;
    
    // Check if the timer is paused (due to submission)
    const timerPausedKey = getLocalStorageKey('timer_paused');
    const isTimerPaused = timerPausedKey ? localStorage.getItem(timerPausedKey) === 'true' : false;
    
    if (savedTimerValue) {
      // We have a saved timer value, use it
      const savedSeconds = parseInt(savedTimerValue, 10);
      logger.debug("Found saved timer value:", savedSeconds, "seconds for question ID:", id, "paused:", isTimerPaused);
      
      // Set the countdown value immediately to update display
      setCountdown(formatSecondsToTime(savedSeconds));
      
      // Call onTimerChange to update parent components
      if (onTimerChange) {
        onTimerChange({
          seconds: savedSeconds,
          displayValue: formatSecondsToTime(savedSeconds)
        });
      }
      
      // Only start the countdown if the timer is not paused (not submitted)
      if (!isTimerPaused && savedSeconds > 0 && !isQuestionSubmitted) {
        // Start the countdown with a small delay to ensure state is updated
        timerId = setTimeout(() => {
          startSimpleCountdown(savedSeconds);
        }, 50);
      }
    } else if (questionTimer && !isQuestionSubmitted) {
      // No saved timer, use the questionTimer from props
      logger.debug("No saved timer, using questionTimer:", questionTimer, "for question ID:", id);
      
      // Don't restart if the timer is already at 0:00 (expired)
      if (countdown === "0:00") {
        return;
      }
      
      // Set the countdown value first
      setCountdown(questionTimer);
      
      // Parse the seconds from the questionTimer
      let seconds;
      if (typeof questionTimer === 'number') {
        seconds = questionTimer;
      } else if (questionTimer.includes(':')) {
        const [minutes, secs] = questionTimer.split(':').map(num => parseInt(num, 10));
        if (!isNaN(minutes) && !isNaN(secs)) {
          seconds = (minutes * 60) + secs;
        }
      } else {
        seconds = parseInt(questionTimer, 10);
      }
      
      if (seconds && seconds > 0) {
        // Save the initial timer value
        saveTimerState(seconds, formatSecondsToTime(seconds));
        
        // Start countdown with a slight delay to ensure state is updated
        timerId = setTimeout(() => {
          startSimpleCountdown(seconds);
        }, 100);
      }
    } else {
      setCountdown(null);
    }
    
    // Clean up on unmount or when dependencies change
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [questionTimer, id, isQuestionSubmitted]);
  
  // Add a new effect to handle question ID changes independently
  useEffect(() => {
    // Get the question ID from either the WebSocket context or the question prop
    const questionId = id || (question && question.id);
    
    if (questionId && interviewId) {
      logger.debug("CodeEditor: Question ID changed to:", questionId);
      
      // Reset timer-related state
      setIsTimeWarningOpen(false);
      
      // Check if we've already shown the warnings for this question
      const warningKey = `warning_5min_shown_${interviewId}_${questionId}`;
      const oneMinWarningKey = `warning_1min_shown_${interviewId}_${questionId}`;
      const fiveMinWarningShown = localStorage.getItem(warningKey) === 'true';
      const oneMinWarningShown = localStorage.getItem(oneMinWarningKey) === 'true';
      
      setHasShownFiveMinWarning(fiveMinWarningShown);
      setHasShownOneMinWarning(oneMinWarningShown);
      
      // DO NOT remove saved timers when question ID changes
      // This allows the timer to persist across page refreshes
      
      // Check if we have saved code for this question
      const storageKey = getLocalStorageKey('code');
      if (storageKey) {
        const savedCode = localStorage.getItem(storageKey);
        if (!savedCode) {
          logger.debug('No saved code found for new question ID, will use default template');
          // If no saved code, we'll let the initialValue effect handle it
        } else {
          logger.debug('Found saved code for question ID:', questionId);
          // Update the editor with the saved code - this should override any initialValue
          setValue(savedCode);
          // Also notify parent of the change
          if (onChangeInputValue) {
            onChangeInputValue(savedCode);
          }
        }
      }
    }
  }, [id, question, interviewId]);
  
  // Prevent timer reset when WebSocket sends new timer value if we already have a saved timer
  useEffect(() => {
    // Only run this effect when questionTimer changes but not on initial mount
    if (questionTimer && timerIntervalRef.current) {
      // If we already have an active timer, don't reset it with the WebSocket value
      logger.debug("Ignoring new timer value from WebSocket as timer is already running");
      // We could optionally compare the values and only use the new one if it's significantly different
    }
  }, [questionTimer]);
  
  // Helper function to properly clean and format boilerplate code
  const cleanBoilerplateCode = (code, languageKey) => {
    if (!code) return '';
    
    // Special handling for SQL
    if (languageKey === 'SQL') {
      // Handle escaped newlines and other special characters
      return code
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\t/g, '\t');
    }
    
    return code;
  };

  // Add a helper function to convert language to proper format for boilerplate lookup
  const getLanguageKey = (languageCode) => {
    const lowercased = languageCode.toLowerCase();
    
    // Special case for SQL to ensure it matches server-side key
    if (lowercased === 'sql') {
      return 'SQL';
    }
    
    // Map other language codes to their proper keys
    const keyMap = {
      'javascript': 'JavaScript',
      'python': 'Python',
      'cpp': 'C++',
      'c++': 'C++',
      'go': 'Go',
      'java': 'Java'
    };
    
    return keyMap[lowercased] || languageCode.charAt(0).toUpperCase() + lowercased.slice(1);
  };

  // Handle initialValue from parent, but prioritize localStorage data
  useEffect(() => {
    // First check if we have saved data in localStorage
    const storageKey = getLocalStorageKey('code');
    const hasSavedCode = storageKey && localStorage.getItem(storageKey);
    
    // Get the saved language, if any
    const questionId = id || (question && question.id);
    const languageKey = interviewId && questionId ? `language_${interviewId}_${questionId}` : null;
    const savedLanguage = languageKey ? localStorage.getItem(languageKey) : null;
    
    logger.debug('Language Selection Debug:', {
      savedLanguage,
      currentLanguage: language,
      codeLanguage,
      hasSavedCode,
      questionId,
      languageKey
    });
    
    // Always store boilerplate codes if available, regardless of whether we have saved code
    if (initialValue && initialValue.boilerplate_code) {
      logger.debug('Boilerplate Code Debug:', {
        availableLanguages: Object.keys(initialValue.boilerplate_code),
        boilerplateContent: initialValue.boilerplate_code,
        currentLanguage: language,
        codeLanguage
      });
      
      setBoilerplateCodes(initialValue.boilerplate_code);
      
      // Get the first available language from boilerplate codes
      const availableLanguages = Object.keys(initialValue.boilerplate_code);
      
      if (availableLanguages.length > 0) {
        // If SQL is available, use it
        if (availableLanguages.includes('SQL')) {
          logger.debug('Setting language to SQL based on boilerplate');
          setLanguage('sql');
          setCodeLanguage('sql');
          if (languageKey) {
            localStorage.setItem(languageKey, 'sql');
          }
        } else {
          // Check if current language is available in boilerplate
          const currentLangKey = getLanguageKey(language);
          if (availableLanguages.includes(currentLangKey)) {
            logger.debug('Keeping current language:', currentLangKey);
            // Don't change the language if it's already set correctly
          } else {
            // Only if current language is not available, use the first available
            const firstLanguage = availableLanguages[0].toLowerCase();
            logger.debug('Current language not available, setting to first available:', firstLanguage);
            setLanguage(firstLanguage);
            setCodeLanguage(firstLanguage);
            if (languageKey) {
              localStorage.setItem(languageKey, firstLanguage);
            }
          }
        }
      }
      
      // Only set the code if we don't have saved code
      if (!hasSavedCode) {
        const langKey = getLanguageKey(language);
        
        logger.debug('Using boilerplate for language:', {
          langKey,
          currentLanguage: language,
          availableBoilerplates: Object.keys(initialValue.boilerplate_code)
        });
        
        // Load the boilerplate code for the language
        const rawCode = initialValue.boilerplate_code[langKey] || 
                      initialValue.boilerplate_code['SQL'] || 
                      initialValue.boilerplate_code['Java'] || 
                      '';
                      
        logger.debug('Boilerplate code selection:', {
          selectedKey: langKey,
          foundCode: !!rawCode,
          availableKeys: Object.keys(initialValue.boilerplate_code)
        });
        
        const cleanedCode = cleanBoilerplateCode(rawCode, langKey);
        
        // Update the editor with the saved language's boilerplate
        localStorage.removeItem(savedLanguage);
        setValue(cleanedCode);
        onChangeInputValue(cleanedCode);
      }
      return; // Exit early after handling boilerplate code
    }
    
    // If we have saved code, use it instead of initialValue for the editor content
    if (hasSavedCode) {
      logger.debug('Found saved code in localStorage, using that for editor content');
      // The actual code will already be loaded from localStorage in the useState initialization
      return;
    }
    
    // No saved code and no boilerplate, so use initialValue from parent
    logger.debug('No saved code found, using initialValue from parent for editor content');
    
    if (initialValue) {
      // Handle if initialValue is a string (for backward compatibility)
      if (typeof initialValue === 'string') {
        setValue(initialValue);
        onChangeInputValue(initialValue);
      } else {
        setValue(initialValue.current_code || initialValue);
        onChangeInputValue(initialValue.current_code || initialValue);
      }
    }
  }, [initialValue, id, question, language]);

  const onMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Add Monaco-specific paste prevention
    editor.onKeyDown((e) => {
      // Detect Ctrl+V or Cmd+V
      if ((e.ctrlKey || e.metaKey) && e.keyCode === monaco.KeyCode.KeyV) {
        // Allow copy/paste for specific question IDs
        if (shouldAllowCopyPaste()) {
          logger.debug('Monaco editor paste allowed for question ID:', id);
          return;
        }
        logger.debug('Monaco editor paste shortcut detected');
        e.preventDefault();
        e.stopPropagation();
        toast.error('Pasting code is not allowed in this editor');
      }
    });
    
    // Disable paste command
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, () => {
      // Allow copy/paste for specific question IDs
      if (shouldAllowCopyPaste()) {
        logger.debug('Monaco editor paste command allowed for question ID:', id);
        return;
      }
      logger.debug('Monaco editor paste command intercepted');
      toast.error('Pasting code is not allowed in this editor');
    });
    editor.focus();
    
    // Check if we're in development mode or if copy/paste is allowed for this question
    const isDevMode = import.meta.env.MODE === 'development';
    const allowCopyPaste = shouldAllowCopyPaste();
    
    if (!isDevMode && !allowCopyPaste) {
      // 1. Prevent copy and paste through DOM events - only in production mode
      const editorElement = editor.getContainerDomNode();
      if (editorElement) {
        // Add listeners to both the container and the internal textarea
        const elements = [
          editorElement,
          editorElement.querySelector('textarea'),
          editorElement.querySelector('.monaco-editor')
        ];

        // Store event listeners for cleanup
        const eventListeners = [];

        elements.forEach(element => {
          if (element) {
            const copyHandler = (e) => {
              e.stopPropagation();
              e.preventDefault();
              return false;
            };
            
            const pasteHandler = (e) => {
              e.stopPropagation();
              e.preventDefault();
              return false;
            };
            
            const contextMenuHandler = (e) => {
              e.preventDefault();
              return false;
            };

            element.addEventListener('copy', copyHandler, true);
            element.addEventListener('paste', pasteHandler, true);
            element.addEventListener('contextmenu', contextMenuHandler, true);
            
            // Store references for cleanup
            eventListeners.push({
              element,
              events: [
                { name: 'copy', handler: copyHandler },
                { name: 'paste', handler: pasteHandler },
                { name: 'contextmenu', handler: contextMenuHandler }
              ]
            });
          }
        });

        // Cleanup function for event listeners
        const cleanupEditorListeners = () => {
          eventListeners.forEach(({ element, events }) => {
            events.forEach(({ name, handler }) => {
              element.removeEventListener(name, handler, true);
            });
          });
        };

        // Store cleanup function on editor instance for later use
        editor._cleanupListeners = cleanupEditorListeners;
      }

      // 2. Disable keyboard shortcuts - only in production mode
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyC, () => { });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, () => { });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyX, () => { });

      // 3. Update editor options - apply restrictive options only in production mode
      editor.updateOptions({
        readOnly: false,
        copyWithSyntaxHighlighting: false,
        contextmenu: false,
        quickSuggestions: false,
        suggestOnTriggerCharacters: false,
        parameterHints: {
          enabled: false
        },
        suggest: {
          showKeywords: true,
          showSnippets: true,
          showClasses: true,
          showFunctions: true,
          showVariables: true
        },
        extraEditorClassName: 'no-copy-paste',
      });
    }
  };
  
  // Add cleanup for editor event listeners when component unmounts
  useEffect(() => {
    return () => {
      // Clean up editor event listeners
      if (editorRef.current && editorRef.current._cleanupListeners) {
        editorRef.current._cleanupListeners();
      }
      
      // Ensure timer is cleaned up
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Keyboard event handler to prevent copy-paste shortcuts
  useEffect(() => {
    const preventCopyPaste = (e) => {
      // Allow copy/paste for specific question IDs
      if (shouldAllowCopyPaste()) {
        return true;
      }
      
      // Only prevent in the editor or when editor has focus
      const isEditorFocused = document.activeElement && 
          (document.activeElement.closest('.monaco-editor') || 
           document.activeElement.classList.contains('monaco-editor'));
               
      // Check for Ctrl+V or Cmd+V (paste)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && isEditorFocused) {
        logger.debug('Paste shortcut detected');
        e.preventDefault();
        e.stopPropagation();
        toast.error('Pasting code is not allowed in this editor');
        return false;
      }
      return true;
    };

    // Add the event listener to the window
    window.addEventListener('keydown', preventCopyPaste, true);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', preventCopyPaste, true);
    };
  }, [shouldAllowCopyPaste]);
  
  // Global paste prevention
  useEffect(() => {
    // Function to handle paste events globally
    const preventPaste = (e) => {
      // Allow copy/paste for specific question IDs
      if (shouldAllowCopyPaste()) {
        return;
      }
      
      // Only prevent paste in the editor area
      if (e.target.closest('.monaco-editor')) {
        logger.debug('Global paste attempt detected');
        e.preventDefault();
        e.stopPropagation();
        toast.error('Pasting code is not allowed in this editor');
      }
    };
    
    // Add global event listener
    document.addEventListener('paste', preventPaste, true);
    
    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('paste', preventPaste, true);
    };
  }, [shouldAllowCopyPaste]);
  
  // Additional protection against programmatic paste attempts
  useEffect(() => {
    // Skip all protections if copy/paste is allowed for this question
    if (shouldAllowCopyPaste()) {
      return;
    }
    
    // Override clipboard API methods when editor is focused
    const originalExecCommand = document.execCommand;
    const originalGetClipboard = navigator.clipboard?.readText;
    
    document.execCommand = function(command) {
      if (command.toLowerCase() === 'paste') {
        const isEditorFocused = document.activeElement && 
            (document.activeElement.closest('.monaco-editor') || 
             document.activeElement.classList.contains('monaco-editor'));
               
        if (isEditorFocused) {
          logger.debug('execCommand paste attempt blocked');
          toast.error('Pasting code is not allowed in this editor');
          return false;
        }
      }
      return originalExecCommand.apply(this, arguments);
    };
    
    if (navigator.clipboard && navigator.clipboard.readText) {
      navigator.clipboard.readText = function() {
        const isEditorFocused = document.activeElement && 
            (document.activeElement.closest('.monaco-editor') || 
             document.activeElement.classList.contains('monaco-editor'));
               
        if (isEditorFocused) {
          logger.debug('Clipboard API readText blocked');
          toast.error('Pasting code is not allowed in this editor');
          return Promise.reject(new Error('Paste not allowed'));
        }
        return originalGetClipboard.apply(this, arguments);
      };
    }
    
    return () => {
      // Restore original methods on cleanup
      document.execCommand = originalExecCommand;
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText = originalGetClipboard;
      }
    };
  }, [shouldAllowCopyPaste]);

  const onSelect = (language) => {
    setLanguage(language);
    setCodeLanguage(language);
    
    // Save the selected language to localStorage with proper namespacing
    const questionId = id || (question && question.id);
    if (interviewId && questionId) {
      const storageKey = `language_${interviewId}_${questionId}`;
      localStorage.setItem(storageKey, language);
      logger.debug(`Saved language ${language} to localStorage with key: ${storageKey}`);
    }
    
    // Notify parent component about language change if callback exists
    if (onLanguageChange) {
      onLanguageChange(language);
    }
    
    // If we have boilerplate codes from either initialValue or WebSocket
    if (Object.keys(boilerplateCodes).length > 0) {
      // Get the proper language key for boilerplate lookup
      const langKey = getLanguageKey(language);
      logger.debug(`Changing language to ${language}, using key ${langKey} for boilerplate lookup`);
      
      // Find the best matching boilerplate code
      const rawCode = boilerplateCodes[langKey] || 
                     boilerplateCodes['SQL'] || 
                     boilerplateCodes['Java'] || 
                     '';
                     
      const cleanedCode = cleanBoilerplateCode(rawCode, langKey);
      logger.debug(`Found boilerplate code for ${langKey}:`, rawCode ? 'yes' : 'no');
      
      // Always update the code to use the selected language's boilerplate
      setValue(cleanedCode);
      onChangeInputValue(cleanedCode);
    } else {
      // Fallback to old behavior if no boilerplate codes
      logger.debug('No boilerplate codes available, using default template');
      setValue(getInitialValue(language, question_remarks));
      onChangeInputValue(getInitialValue(language, question_remarks));
    }
  };

  const handleHintButtonClick = () => {
    if (!isHintClicked) {
      // Check if hint limit has been reached
      if (hintsUsed >= MAX_HINTS_PER_INTERVIEW) {
        toast.error(`You have reached the maximum limit of ${MAX_HINTS_PER_INTERVIEW} hints per interview`);
        return;
      }
      setIsHintModalOpen(true);
    }
  };

  const handleHintSelection = (isDebugHint) => {
    setIsHintClicked(true);
    setIsHintModalOpen(false);

    if (isDebugHint) {
      sendDebugHintRequest(value, id);
    } else {
      sendApproachHintRequest(value, id);
    }

    // Reset after 4 seconds
    setTimeout(() => {
      setIsHintClicked(false);
    }, 4000);
  };

  const handleCloseHintModal = () => {
    setIsHintModalOpen(false);
  };

  const handleExplanationRequest = () => {
    if (!isExplainDisabled) {
      setIsExplainDisabled(true);
      sendExplanationRequest(id);
      setIsExplainClicked(true);
      setIsSubmitted(false);

      setTimeout(() => {
        setIsExplainDisabled(false);
      }, 4000);
    }
  };

  const onSkip = () => {
    if (!isSkipDisabled) {
      setIsSkipModalOpen(true);
    }
  };

  const handleSkipConfirm = () => {
    try {
      setIsSkipDisabled(true);
      sendSkipRequest(id);
      setIsSkipModalOpen(false);
      
      // Use the resetTimer function for consistency
      resetTimer();

      setTimeout(() => {
        setIsSkipDisabled(false);
      }, 4000);
    } catch (error) {
      logger.error('Error in handleSkipConfirm:', error);
      setIsSkipModalOpen(false);
      setIsSkipDisabled(false);
    }
  };

  const handleCloseSkipModal = () => {
    setIsSkipModalOpen(false);
  };

  // You can also add a helper function to check connection status
  const checkWebSocketStatus = () => {
    if (!ws) {
      return false;
    }

    const states = {
      0: 'CONNECTING',
      1: 'OPEN',
      2: 'CLOSING',
      3: 'CLOSED'
    };

    return ws.readyState === WebSocket.OPEN;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    if (isSubmitted) {
      logger.debug('Auto-late data:', {
        value: value,
        language: language,
        id: id,
        question: question,
        interviewId: interviewId
      });
      
      return;
    }
    if (e) e.preventDefault();

    try {
      // Check if WebSocket is connected
      if (!checkWebSocketStatus()) {
        toast.error('Not connected to server. Please refresh the page.');
        return;
      }

      setIsSubmitting(true); // Show loading state during submission
      setIsExplainClicked(false);

      // Add error handling for onSubmit
      if (!onSubmit) {
        throw new Error('Submit handler is not defined');
      }
      
      // Log the submission data
      logger.debug('Auto-submission data:', {
        value: value,
        language: language,
        id: id,
        question: question,
        interviewId: interviewId
      });
      
      // Define a callback function to handle successful submission
      const handleSuccessfulSubmission = (response) => {
        if (response && response.success) {
          // Only mark as submitted if the API response was successful
          setIsSubmitted(true); // Permanently disable button
          
          // Store the submitted question ID directly without nesting arrays
          setSubmittedQuestions(prev => ({
            ...prev,
            [id]: true  // Mark this question as submitted
          }));

          // Clear all language-related localStorage keys for this question
          if (interviewId && id) {
            const languageKey = `language_${interviewId}_${id}`;
            localStorage.removeItem(languageKey);
            logger.debug('Cleared language key:', languageKey);
          }

          toast.success('Code submitted successfully!');
          
          // Pause the timer instead of resetting it
          pauseTimer();
        }
      };
      
      // Call the onSubmit function with the callback
      await onSubmit(e, handleSuccessfulSubmission);

    } catch (error) {
      logger.error('Error during submission:', error);
      toast.error('Failed to submit code. Please try again.');
      setIsSubmitted(false);
    } finally {
      setIsSubmitting(false); // Always hide loading state
    }
  };

  // Reset states when question changes (id changes)
  useEffect(() => {
    // Don't reset isSubmitted if the question has been submitted
    // Only reset other states
    setIsExplainClicked(false);
    setIsExplainDisabled(false);
    
    // Reset isSubmitted when question changes
    setIsSubmitted(false);
    
    // Only reset submitDisabled if the question has NOT been submitted
    if (!isQuestionSubmitted) {
      setIsSubmitDisabled(false);
    } else {
      // If question is already submitted, ensure submit button stays disabled
      setIsSubmitDisabled(true);
    }
  }, [id, isQuestionSubmitted]);

  // Effect to update value when initialValue changes

  const handleClose = () => {
    setHasShownWarning(false);
    setIsSubmitEnabled(false);
    setIsExplainClicked(false);
    // Don't reset isSubmitted when closing the editor
    // The submission status should persist
    
    // We don't want to clear the timer state here because the user might
    // just be closing the editor and wants to come back to it later
    
    localStorage.removeItem(`explained_${id}`);
    onClose();
  };

  // Auto-save code function
  const autoSaveCode = useCallback(async () => {
    logger.debug('Auto-save triggered', id, valueRef.current);
    if (!id || !valueRef.current) return;
    
    try {
      // Skip auto-save if the code hasn't changed since last save
      const codeHash = btoa(encodeURIComponent(valueRef.current)).substring(0, 10);
      const lastSavedHash = localStorage.getItem(`lastSavedHash_${id}`);
      
      // if (codeHash === lastSavedHash) {
      //   if (import.meta.env.MODE !== 'production') {
      //     logger.debug('Skipping auto-save - code unchanged');
      //   }
      //   return;
      // }
      
      // Get the interview ID from local storage
      // const interviewId = localStorage.getItem('interview_id');
      

      if (_.isObject(valueRef.current)) {
        logger.debug('Value is an object:', valueRef.current);
        return;
      }
      if (!interviewId) {
        logger.debug('Interview ID not found in localStorage');
        return;
      }
      
      logger.debug('Interview ID>>>:', interviewId);
      const payload = {
        interview_id: interviewId,
        question_id: id,
        answer: valueRef.current,
        language: languageRef.current,
        question: question
      };

      logger.debug('Auto-save payload:', payload);
      
      // Use the correct API URL format
      const apiUrl = BASE_URL.startsWith('http') 
        ? `${BASE_URL}/api/auto-save-code` 
        : `https://${BASE_URL}/api/auto-save-code`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
       // credentials: "include",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        // Store the hash of the saved code
        localStorage.setItem(`lastSavedHash_${id}`, codeHash);
        
        if (import.meta.env.MODE !== 'production') {
          logger.debug('Code auto-saved successfully');
        }
      } else {
        const errorData = await response.json();
        if (import.meta.env.MODE !== 'production') {
          logger.error('Auto-save failed:', errorData);
        }
      }
    } catch (error) {
      if (import.meta.env.MODE !== 'production') {
        logger.error('Error auto-saving code:', error);
      }
    }
  }, [id, languageRef, valueRef, BASE_URL, interviewId, question]);
  
  // Set up interval for auto-saving every 1 minute
  useEffect(() => {
    // Initial auto-save when component mounts
    // autoSaveCode();
    logger.debug('Auto-save interval set up useeffect')
    
    // Set up interval for auto-saving
    const autoSaveInterval = setInterval(() => {
      // Don't auto-save if the timer is about to expire
      const seconds = parseTimeToSeconds(countdownRef.current);
      if (seconds > 10) { // Only auto-save if more than 10 seconds remain
        autoSaveCode();
      }
    }, 3 * 60 * 1000); // 1 minute in milliseconds
    
    // Clean up interval when component unmounts or dependencies change
    return () => {
      clearInterval(autoSaveInterval);
    };
  }, [autoSaveCode]); // Only depend on autoSaveCode which is memoized

  const handleCloseTimeWarning = () => {
    setIsTimeWarningOpen(false);
    
    // Permanently record that we've shown the warning for this question
    if (id) {
      if (warningTimeMinutes === 5) {
        localStorage.setItem(`warning_5min_shown_${id}`, 'true');
        setHasShownFiveMinWarning(true);
      } else if (warningTimeMinutes === 1) {
        localStorage.setItem(`warning_1min_shown_${id}`, 'true');
        setHasShownOneMinWarning(true);
      }
    }
  };

  // Function to reset timer completely
  const resetTimer = () => {
    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Clear timer state from localStorage
    clearTimerState();
    
    // Reset the countdown
    setCountdown(null);
    
    // Note: we don't reset hasShownFiveMinWarning here to preserve the warning state
  };

  // Add effect to update language when codeLanguage changes from WebSocket
  useEffect(() => {
    // Check if we have a saved language preference
    const questionId = id || (question && question.id);
    const languageKey = interviewId && questionId ? `language_${interviewId}_${questionId}` : null;
    const savedLanguage = languageKey ? localStorage.getItem(languageKey) : null;
    
    if (codeLanguage) {
      // If we receive 'all', default to a specific language unless user already has a preference
      if (codeLanguage === 'all') {
        if (!savedLanguage) {
          // No saved preference, so set a default language
          const defaultLang = 'javascript'; // Choose appropriate default
          logger.debug('WebSocket sent "all", defaulting to:', defaultLang);
          setLanguage(defaultLang);
          
          // Save this preference for future use
          if (languageKey) {
            localStorage.setItem(languageKey, defaultLang);
          }
        } else {
          logger.debug('Using saved language preference:', savedLanguage);
        }
      } 
      // If we have a specific language from WebSocket, always use it (prioritize server requirement)
      else {
        const newLanguage = codeLanguage.toLowerCase();
        logger.debug('Setting language from WebSocket (server requirement):', newLanguage);
        setLanguage(newLanguage);
        
        // Save this language preference for this specific question
        if (languageKey) {
          localStorage.setItem(languageKey, newLanguage);
        }
      }
    }
  }, [codeLanguage, id, question, interviewId]);

  // Update parent with timer value whenever countdown changes
  useEffect(() => {
    if (typeof countdown === 'string') {
      // If countdown is a string (mm:ss format), convert to seconds first
      const seconds = parseTimeToSeconds(countdown);
      if (onTimerChange) {
        onTimerChange({
          seconds: seconds,
          displayValue: countdown
        });
      }
    } else if (typeof countdown === 'number') {
      // If countdown is already a number, pass it directly
      if (onTimerChange) {
        onTimerChange({
          seconds: countdown,
          displayValue: formatSecondsToTime(countdown)
        });
      }
    }
  }, [countdown, onTimerChange]);
  
  // Add an effect to initialize SQL mode when initialValue is set
  useEffect(() => {
    // We're not setting isSqlMode at all from initialValue to prevent language locking
    // The SQL mode is only used for server-side features, not for locking UI
  }, [initialValue]);

  // Add effect to detect SQL mode but not lock language selection
  useEffect(() => {
    if (codeLanguage) {
      const isSql = codeLanguage.toLowerCase() === 'sql';
      
      // Log detection but don't lock the language selector
      if (isSql) {
        logger.debug("SQL language detected, but language selection remains unlocked");
      }
    }
  }, [codeLanguage]);

  // Keep isSubmitted in sync with isQuestionSubmitted
  useEffect(() => {
    if (isQuestionSubmitted && !isSubmitted) {
      setIsSubmitted(true);
    }
  }, [isQuestionSubmitted, isSubmitted]);

  // Add a new function to pause timer without clearing localStorage
  const pauseTimer = () => {
    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // But don't reset the timer state or clear from localStorage
    // This keeps the timer paused at its current value
    logger.debug('Timer paused at:', countdownRef.current);
    
    // Save the paused state to localStorage
    const timerPausedKey = getLocalStorageKey('timer_paused');
    if (timerPausedKey) {
      localStorage.setItem(timerPausedKey, 'true');
    }
  };

  // Add effect to handle new language keys for new questions
  useEffect(() => {
    if (id && interviewId) {
      const languageKey = `language_${interviewId}_${id}`;
      const savedLanguage = localStorage.getItem(languageKey);
      
      logger.debug('New question language key:', languageKey);
      logger.debug('Saved language for new question:', savedLanguage);
      
      if (savedLanguage) {
        logger.debug('Setting editor value for language:', savedLanguage);
        // Get the proper language key for boilerplate lookup
        const langKey = getLanguageKey(savedLanguage);
        
        // Find the best matching boilerplate code
        const rawCode = boilerplateCodes[langKey] || 
                       boilerplateCodes['SQL'] || 
                       boilerplateCodes['Java'] || 
                       '';
                           
        const cleanedCode = cleanBoilerplateCode(rawCode, langKey);
        
        // Update the editor with the saved language's boilerplate
        localStorage.removeItem(savedLanguage)
        setValue(cleanedCode);
        onChangeInputValue(cleanedCode);
      }
    }
  }, [id, interviewId, boilerplateCodes]);

  

  return (
    <ChakraProvider theme={theme}>
      <Box mt={4} px={4} h={"100vh"} overflowY={"scroll"}>
        {/* Time Warning Modal */}
        <AlertDialog
          isOpen={isTimeWarningOpen}
          leastDestructiveRef={cancelRef}
          onClose={handleCloseTimeWarning}
          isCentered
        >
          <AlertDialogOverlay style={{ zIndex: 10000 }}>
            <AlertDialogContent
              bg="yellow.50"
              borderColor="yellow.500"
              borderWidth="2px"
            >
              <AlertDialogHeader
                fontSize="lg"
                fontWeight="bold"
                color="yellow.700"
              >
                Time Warning
              </AlertDialogHeader>

              <AlertDialogBody>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  gap={3}
                >
                  <Box color="yellow.500" fontSize="3xl">
                    ⏱️
                  </Box>
                  <Text fontWeight="bold" color="yellow.800">
                    {warningTimeMinutes} minutes remaining!
                  </Text>
                  <Text color="yellow.700">
                    You have {warningTimeMinutes} minutes left to complete and
                    submit your code. Please finish your solution and submit it
                    before time runs out.
                  </Text>
                </Box>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  onClick={handleCloseTimeWarning}
                  colorScheme="yellow"
                >
                  I understand
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <div style={{ zIndex: 1000000 }}>
          <Box
            position="fixed"
            top={2}
            right={2}
            p={2}
            borderRadius="md"
            bg={ws?.readyState === WebSocket.OPEN ? "green.500" : "red.500"}
            color="white"
            fontSize="sm"
            zIndex="1"
          >
            {ws?.readyState === WebSocket.OPEN ? "Connected" : "Disconnected"}
          </Box>

          {/* Add hint usage counter */}
          {/* <Box
            position="fixed"
            top={12}
            right={2}
            p={2}
            borderRadius="md"
            bg={hintsUsed >= MAX_HINTS_PER_INTERVIEW ? "red.500" : "blue.500"}
            color="white"
            fontSize="sm"
            zIndex="1"
          >
            Hints: {hintsUsed}/{MAX_HINTS_PER_INTERVIEW}
          </Box> */}
        </div>

        <Box position={"relative"} mt={10}>
          <HStack spacing={4}>
            <Box w={isMobile ? "100%" : "70%"}>
              <LanguageSelector
                language={language}
                onSelect={onSelect}
                disabled={false}
              />
              <Editor
                options={{
                  minimap: {
                    enabled: false,
                  },
                  contextmenu: false,
                  quickSuggestions: true,
                  suggestOnTriggerCharacters: true,
                  parameterHints: {
                    enabled: true,
                  },
                  suggest: {
                    showKeywords: true,
                    showSnippets: true,
                    showClasses: true,
                    showFunctions: true,
                    showVariables: true,
                  },
                  copyWithSyntaxHighlighting: false,
                  selectionClipboard: false,
                  find: {
                    addExtraSpaceOnTop: false,
                    autoFindInSelection: "never",
                    seedSearchStringFromSelection: false,
                  },
                }}
                height="64vh"
                theme="vs-dark"
                language={language}
                value={value}
                onMount={onMount}
                onChange={(value) => {
                  setValue(value);

                  // Save to localStorage immediately on each change
                  const storageKey = getLocalStorageKey("code");
                  if (storageKey) {
                    try {
                      localStorage.setItem(storageKey, value);
                      logger.debug(
                        "Auto-saved code to localStorage with key:",
                        storageKey
                      );
                    } catch (error) {
                      logger.error("Error saving code on change:", error);
                    }
                  } else {
                    logger.warn("Could not save code - missing storage key");
                  }

                  onChangeInputValue(value);
                }}
              />
            </Box>
          </HStack>
          <Box position={"absolute"} right={"30vw"} mb={4} mt={4}>
            <HStack spacing={4}>
              {/* <Button
                onClick={handleClose}
                variant="outline"
                colorScheme="red"
                mr={4}
              >
                Close
              </Button> */}
              {!shouldHideButtons() && (
                <Button
                  variant="solid"
                  colorScheme="blue"
                  mr={4}
                  onClick={handleHintButtonClick}
                  isDisabled={
                    isHintClicked ||
                    isQuestionSubmitted ||
                    isHintDisabled ||
                    hintsUsed >= MAX_HINTS_PER_INTERVIEW
                  }
                  title={
                    hintsUsed >= MAX_HINTS_PER_INTERVIEW
                      ? `Maximum limit of ${MAX_HINTS_PER_INTERVIEW} hints reached`
                      : ""
                  }
                >
                  Hint {hintsUsed}/{MAX_HINTS_PER_INTERVIEW}
                </Button>
              )}
              <Tooltip label="Explain the approach of the code you have written">
                {/* <Button 
                  variant="solid" 
                  colorScheme="purple" 
                  mr={4} 
                  onClick={handleExplanationRequest}
                  isDisabled={isExplainDisabled}
                >
                  Explain
                </Button> */}
              </Tooltip>
              <Tooltip
                label="You have to click the Explain button and explain the code first before submitting."
                isDisabled={!isExplainClicked}
              >
                <span>
                  {!shouldHideButtons() && (
                    <Button
                      variant="solid"
                      colorScheme="green"
                      onClick={handleSubmit}
                      mr={4}
                      isDisabled={
                        isSubmitted || isQuestionSubmitted || isSubmitDisabled
                      } // Added isSubmitDisabled to disable button
                      isLoading={isSubmitting}
                      loadingText="Submitting..."
                    >
                      {isSubmitted || isQuestionSubmitted
                        ? "Submitted"
                        : "Submit"}
                    </Button>
                  )}
                </span>
              </Tooltip>
              {!shouldHideButtons() && (
                <Button
                  variant="solid"
                  colorScheme="gray"
                  onClick={onSkip}
                  mr={4}
                  isDisabled={isSkipDisabled}
                >
                  Skip
                </Button>
              )}
            </HStack>
          </Box>
        </Box>
        {/* Always render Output component regardless of language */}
        <Output
          editorRef={editorRef}
          language={language}
          onRun={autoSaveCode}
        />
      </Box>
      <ConfirmationModal
        isOpen={isSkipModalOpen}
        onClose={handleCloseSkipModal}
        onConfirm={handleSkipConfirm}
        modalType="skip_question"
      />
      <ConfirmationModal
        isOpen={isHintModalOpen}
        onClose={handleCloseHintModal}
        onConfirm={() => handleHintSelection(true)}
        modalType="hint_selection"
        cancelCallback={() => handleHintSelection(false)}
      />
    </ChakraProvider>
  );
};

export default CodeEditor;
